import { RemoteDataSource, Settings } from "@/datasources/remote_datasource";
import { Failure } from "@/usecases/repository";

/** Valores aceitos pela API (enum textual) para o tipo de instituição. */
export const TIPO_INSTITUICAO_OPCOES = [
  "Governo Municipal",
  "Governo Estadual",
  "Governo Federal",
  "Legislativo Municipal",
  "Legislativo Estadual",
  "Legislativo Federal",
  "Universidade",
  "Terceiro Setor",
  "Outros",
] as const;

/** Valores aceitos pela API (enum textual) para o cargo. */
export const CARGO_OPCOES = [
  "Prefeito/Prefeita",
  "Vice-Prefeito/Vice-Prefeita",
  "Chefe de Gabinete",
  "Procurador/Assessor Jurídico",
  "Assessor/Assessora Técnica",
  "Secretário/Secretária",
  "Gestor/Gestora",
  "Diretor/Diretora de áreas/departamentos",
  "Gerente",
  "Coordenador/Coordenadora",
  "Controlador/Controladora",
  "Auditor/Auditora",
  "Analista",
  "Assistente",
  "Estagiário/Estagiária",
  "Outros",
] as const;

/** Valores aceitos pela API (enum textual) para o tamanho da cidade. */
export const TAMANHO_CIDADE_OPCOES = [
  "De 0 a 40 mil habitantes",
  "De 40 a 70 mil habitantes",
  "De 70 a 150 mil habitantes",
  "De 150 a 250 mil habitantes",
  "De 250 a 500 mil habitantes",
  "Acima de 500 mil habitantes",
] as const;

/** Valores aceitos pela API (enum textual) para o motivo do contato. */
export const MOTIVO_CONTATO_OPCOES = [
  "Quero acrescentar no plano de governo um projeto de inovação com foco na transparência e participação",
  "Quero implementar uma política pública de participação",
  "Quero implementar um programa de Gestão Pública Colaborativa e Participação",
  "Quero um sistema de atendimento ao cidadão",
  "Quero fazer consultas para população",
  "Quero disponibilizar serviços digitais a população",
  "Trabalho no poder legislativo e quero implementar o colab na cidade",
  "Sou pré-candidato",
  "Sou cidadão",
  "Quero ser parceiro",
  "Outro motivo",
] as const;

export type TipoInstituicao = (typeof TIPO_INSTITUICAO_OPCOES)[number];
export type Cargo = (typeof CARGO_OPCOES)[number];
export type TamanhoCidade = (typeof TAMANHO_CIDADE_OPCOES)[number];
export type MotivoContato = (typeof MOTIVO_CONTATO_OPCOES)[number];

export interface SolicitarContatoComercialRequest {
  nome: string;
  email: string;
  celular: string;
  tipoInstituicao: TipoInstituicao | "";
  empresa: string;
  cargo: Cargo | "";
  estado: string;
  cidade: string;
  tamanhoCidade: TamanhoCidade | "";
  motivoContato: MotivoContato | "";
  mensagem?: string;
}

class LeadService {
  private readonly remoteDataSource: RemoteDataSource;

  constructor() {
    this.remoteDataSource = new RemoteDataSource(Settings.urlBase);
  }

  private validar(request: SolicitarContatoComercialRequest): string[] {
    const errors: string[] = [];

    if (!request.nome?.trim()) errors.push("O nome é obrigatório.");
    if (!request.email?.trim()) {
      errors.push("O e-mail é obrigatório.");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(request.email.trim())) {
      errors.push("Informe um e-mail válido.");
    }
    if (!request.celular?.trim()) errors.push("O celular é obrigatório.");
    if (!request.tipoInstituicao) errors.push("O tipo de instituição é obrigatório.");
    if (!request.empresa?.trim()) errors.push("A empresa/órgão é obrigatório.");
    if (!request.cargo) errors.push("O cargo é obrigatório.");
    if (!request.estado?.trim()) errors.push("O estado é obrigatório.");
    if (!request.cidade?.trim()) errors.push("A cidade é obrigatória.");
    if (!request.tamanhoCidade) errors.push("O tamanho da cidade é obrigatório.");
    if (!request.motivoContato) errors.push("O motivo do contato é obrigatório.");

    return errors;
  }

  /**
   * Solicita contato comercial (lead).
   * POST /urbnet/v1/leads
   */
  async solicitarContatoComercial(
    request: SolicitarContatoComercialRequest,
  ): Promise<{ success: true } | { failure: Failure }> {
    const errors = this.validar(request);
    if (errors.length > 0) {
      return { failure: new Failure({ messages: errors }) };
    }

    const body: Record<string, unknown> = {
      nome: request.nome.trim(),
      email: request.email.trim(),
      celular: request.celular.trim(),
      tipoInstituicao: request.tipoInstituicao,
      empresa: request.empresa.trim(),
      cargo: request.cargo,
      estado: request.estado.trim(),
      cidade: request.cidade.trim(),
      tamanhoCidade: request.tamanhoCidade,
      motivoContato: request.motivoContato,
    };

    const mensagem = request.mensagem?.trim();
    if (mensagem) {
      body.mensagem = mensagem;
    }

    const response = await this.remoteDataSource.post<unknown>({
      endpoint: "v1/leads",
      body,
      headers: {
        Accept: "application/force+json",
      },
      requiredToken: false,
    });

    if ("failure" in response) {
      return response;
    }

    return { success: true };
  }
}

export const leadService = new LeadService();
